import type { TypedDocumentNode } from '@apollo/client';
import { GET_WAREHOUSE_INFO, useWarehouseLocations } from 'src/api/warehouses.api';
import { type SectionState, sectionStateOf } from 'src/modules/entities/deferred';
import { preloadRouteQuery } from 'src/routing/routePreload';
import type { AppRouterContext } from 'src/routing/routerAccess';
import { useRouteLoaderQuery } from 'src/routing/useRouteLoaderQuery';
import type { WarehouseInfoQuery, WarehouseInfoQueryVariables } from 'types/graphql';

export type WarehouseSectionName = 'warehouse' | 'locations';

export const WAREHOUSE_SECTION_NAMES: readonly WarehouseSectionName[] = ['warehouse', 'locations'];

/** The warehouse header read; the warehouse write hooks refetch it by this document. */
const WAREHOUSE_INFO: TypedDocumentNode<WarehouseInfoQuery, WarehouseInfoQueryVariables> = GET_WAREHOUSE_INFO;

/** The route loader's read; `useWarehouseSections` reads the same document and variables. */
export function preloadWarehouse(context: AppRouterContext, warehouseId: number) {
  return preloadRouteQuery(context, WAREHOUSE_INFO, { warehouseId }, { fetchPolicy: 'cache-and-network' });
}

/** The warehouse header read (the loader's ref when there is one) and the locations read, each with its own state. */
export function useWarehouseSections(warehouseId: number) {
  const info = useRouteLoaderQuery(WAREHOUSE_INFO, { warehouseId });
  const locations = useWarehouseLocations({ warehouseId });

  const states: Readonly<Record<WarehouseSectionName, SectionState>> = {
    warehouse: sectionStateOf('warehouse', { ready: info.data !== undefined, error: info.error }),
    locations: sectionStateOf('locations', { ready: locations.ready, error: locations.error }),
  };

  return {
    warehouse: info.data?.warehouse,
    locations: locations.locations,
    states,
    /** Render once beside the content: it reads the loader's ref and renders nothing. */
    reader: info.reader,
    refetchWarehouse: () => info.refetch(),
    retry: (name: WarehouseSectionName): Promise<unknown> =>
      name === 'warehouse' ? info.refetch() : locations.refetch(),
  };
}

export type WarehouseSectionsView = ReturnType<typeof useWarehouseSections>;
